import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useDispatch, useSelector } from "react-redux";
import RecordingCell from "../common/RecordingCell";
import SipTraceLadderDiagram from "./SipTraceLadderDiagram";
import {
  getAdminCdrDetails,
  getAdminCdrSipLog,
  resetAdminCdrDetails,
} from "../../redux/actions/reportAction";
import { formatDateTimeJSXModal } from "../../user_portals/redirect_portal/report/utils/dateFormatterModal";
import { exportCdrDetailsToCSV } from "./utils/csvExporter";
import { getCurrentTzAbbreviation } from "../../utils/cdrTimezone";

const CHANNEL_VAR_HIGHLIGHTS = [
  "sip_status",
  "sip_bye_h_reason",
  "sip_hangup_disposition",
  "sip_invite_failure_status",
  "sip_invite_failure_phrase",
  "hangup_cause",
  "hangup_cause_q850",
  "Cause",
  "Cause-txt",
  "DialStatus",
  "Disposition",
  "AMAFlags",
  "LastApplication",
  "LastData",
];

const SUMMARY_FIELDS_LEFT = [
  ["Caller ID", "src"],
  ["Caller Name", "clid"],
  ["DID Number", "did_number"],
  ["Destination", "dst"],
  ["Forwarded", "forwarded_number"],
];

const SUMMARY_FIELDS_MID = [
  ["Username", "username"],
  ["Campaign", "campaign_name"],
  ["Buyer", "buyer_name"],
  ["Status", "status"],
  ["Call Result", "call_result"],
];

const SUMMARY_FIELDS_RIGHT = [
  ["Duration", "duration"],
  ["Bill Sec", "billsec"],
  ["Hangup Source", "hangupsource"],
  ["Call Hangup By", "hangupby"],
  ["Account Code", "accountcode"],
  ["Unique ID", "uniqueid"],
];

function KeyValueRow({ label, value, mono = false, isDark = false }) {
  const display = value === null || value === undefined || value === "" ? "-" : String(value);
  const handleCopy = () => {
    if (display && display !== "-") {
      navigator.clipboard?.writeText(display);
    }
  };
  const labelBg = isDark ? "#141b2b" : "#f9fafb";
  const labelColor = isDark ? "#8899aa" : "#374151";
  const valueColor = isDark ? "#e0e0e0" : "#111827";
  const borderColor = isDark ? "#2a3a4a" : "#f1f5f9";
  const iconColor = isDark ? "#a0b4c8" : "#9ca3af";
  return (
    <TableRow>
      <TableCell
        sx={{
          width: "35%",
          fontSize: "13px",
          fontWeight: 600,
          color: labelColor,
          verticalAlign: "top",
          borderBottom: `1px solid ${borderColor}`,
          padding: "6px 10px",
          backgroundColor: labelBg,
        }}
      >
        {label}
      </TableCell>
      <TableCell
        sx={{
          fontSize: "13px",
          color: valueColor,
          verticalAlign: "top",
          borderBottom: `1px solid ${borderColor}`,
          padding: "6px 10px",
          fontFamily: mono ? "monospace" : "inherit",
          wordBreak: "break-all",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1 }}>{display}</Box>
          {display && display !== "-" && (
            <Tooltip title="Copy">
              <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
                <ContentCopyIcon sx={{ fontSize: 14, color: iconColor }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}

function SectionAccordion({ title, defaultExpanded = false, count, children, isDark = false }) {
  const borderColor = isDark ? "#2a3a4a" : "#e5e7eb";
  const headerBg = isDark ? "#2d5f42" : "#0a140e";
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: "6px !important",
        mb: 1.5,
        backgroundColor: "transparent",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: headerBg,
          color: "#fff",
          minHeight: "40px !important",
          borderRadius: "6px 6px 0 0",
          "& .MuiAccordionSummary-content": { my: "8px !important" },
          "& .MuiSvgIcon-root": { color: "#fff" },
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#fff" }}>
          {title}
          {typeof count === "number" && (
            <Box
              component="span"
              sx={{
                ml: 1,
                fontSize: "11px",
                fontWeight: 500,
                backgroundColor: "rgba(255,255,255,0.2)",
                px: 1,
                py: 0.25,
                borderRadius: "10px",
              }}
            >
              {count}
            </Box>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------
// SIP Logs accordion — lazy-loaded on first expand. Fetches parsed SIP
// messages for this uniqueid from /api/cdrdetails/siplog and renders a
// summary card + a filterable message table + a raw-message drawer.
// ---------------------------------------------------------------------------

const SIP_METHOD_COLOR = (m) => {
  switch ((m || "").toUpperCase()) {
    case "INVITE": return "#1d4ed8";
    case "ACK":    return "#2563eb";
    case "BYE":    return "#7c3aed";
    case "CANCEL": return "#b91c1c";
    case "OPTIONS":return "#6b7280";
    default:       return "#4b5563";
  }
};

const SIP_STATUS_COLOR = (code) => {
  const n = parseInt(code || "0", 10);
  if (n >= 500) return "#b91c1c";
  if (n >= 400) return "#d97706";
  if (n >= 300) return "#7c3aed";
  if (n >= 200) return "#047857";
  if (n >= 100) return "#6b7280";
  return "#4b5563";
};

const isErrorStatus = (code) => {
  const n = parseInt(code || "0", 10);
  return n >= 400;
};

function SipLogsSection({
  uniqueid,
  isDark,
  cardBg,
  borderColor,
  subColor,
  tableHeadBg,
  tableHeadColor,
  tableCellColor,
  titleColor,
}) {
  const dispatch = useDispatch();
  const sipLog = useSelector(
    (state) => state.cdrDetails?.sipLog || { loading: false, data: null, error: null }
  );
  const { loading, data, error } = sipLog;

  const [expanded, setExpanded] = useState(false);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [methodFilter, setMethodFilter] = useState("ALL");
  const [viewMode, setViewMode] = useState("messages"); // "messages" | "ladder"
  const [ladderCallId, setLadderCallId] = useState(null);
  const [rawMsg, setRawMsg] = useState(null);
  const fetchedRef = useRef(false);

  // Reset fetch guard whenever we switch to a different call.
  useEffect(() => {
    fetchedRef.current = false;
    setExpanded(false);
    setRawMsg(null);
    setErrorsOnly(false);
    setMethodFilter("ALL");
    setViewMode("messages");
    setLadderCallId(null);
  }, [uniqueid]);

  const handleExpand = (_evt, next) => {
    setExpanded(next);
    if (next && uniqueid && !fetchedRef.current) {
      fetchedRef.current = true;
      dispatch(getAdminCdrSipLog(uniqueid));
    }
  };

  const messages = data?.messages || [];
  const summary = data?.summary;
  const unavailable = Boolean(data?.unavailable);
  const serverIp = data?.server_ip || "";
  const methodsAvailable = useMemo(() => {
    const set = new Set();
    messages.forEach((m) => {
      if (m.method) set.add(m.method);
    });
    return Array.from(set).sort();
  }, [messages]);

  const filteredMessages = useMemo(() => {
    let rows = messages;
    if (errorsOnly) rows = rows.filter((m) => isErrorStatus(m.status_code));
    if (methodFilter && methodFilter !== "ALL") {
      rows = rows.filter((m) => m.method === methodFilter);
    }
    return rows;
  }, [messages, errorsOnly, methodFilter]);

  const headerBg = isDark ? "#2d5f42" : "#0a140e";
  const chipBg = isDark ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.2)";
  const countForBadge = summary?.message_count ?? messages.length;

  return (
    <Accordion
      expanded={expanded}
      onChange={handleExpand}
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: "6px !important",
        mb: 1.5,
        backgroundColor: "transparent",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: headerBg,
          color: "#fff",
          minHeight: "40px !important",
          borderRadius: "6px 6px 0 0",
          "& .MuiAccordionSummary-content": { my: "8px !important" },
          "& .MuiSvgIcon-root": { color: "#fff" },
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          SIP Logs
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              fontWeight: 500,
              backgroundColor: chipBg,
              px: 0.75,
              py: 0.25,
              borderRadius: "10px",
            }}
          >
            Debug
          </Box>
          {data && !unavailable && (
            <Box
              component="span"
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                backgroundColor: chipBg,
                px: 1,
                py: 0.25,
                borderRadius: "10px",
              }}
            >
              {countForBadge}
            </Box>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <Box sx={{ p: 2, backgroundColor: cardBg }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && error && (
            <Typography sx={{ fontSize: 13, color: isDark ? "#f87171" : "#b91c1c", textAlign: "center", py: 2 }}>
              {error}
            </Typography>
          )}

          {!loading && !error && unavailable && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography sx={{ fontSize: 13, color: subColor, mb: 0.5 }}>
                SIP logs are not available for this call.
              </Typography>
              <Typography sx={{ fontSize: 12, color: subColor }}>
                {data?.reason || "Only the last ~3 days of Asterisk logs are retained on disk."}
              </Typography>
            </Box>
          )}

          {!loading && !error && !unavailable && data && (
            <>
              {/* Summary row */}
              {summary && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 2,
                    p: 1.5,
                    border: `1px solid ${borderColor}`,
                    borderRadius: "6px",
                  }}
                >
                  <SummaryStat label="Messages" value={summary.message_count} isDark={isDark} />
                  <SummaryStat label="Errors" value={summary.error_count} isDark={isDark} accent={summary.error_count > 0 ? "#dc2626" : undefined} />
                  <SummaryStat label="Legs" value={summary.leg_count} isDark={isDark} />
                  <SummaryStat
                    label="Codecs"
                    value={(summary.codecs || []).join(", ") || "—"}
                    isDark={isDark}
                  />
                </Box>
              )}

              {/* Legs detail — one row per Call-ID */}
              {summary?.legs?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: titleColor, mb: 0.5 }}>
                    Legs
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: tableHeadBg }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor }}>Call-ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor }}>Remote IP</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor }}>Carrier</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor }}>User-Agent</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor }}>Codecs</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor }}>Msgs</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.legs.map((leg) => (
                        <TableRow key={leg.call_id}>
                          <TableCell sx={{ fontFamily: "monospace", fontSize: 11, color: tableCellColor, borderBottomColor: borderColor, wordBreak: "break-all" }}>
                            {leg.call_id || "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{leg.remote_ip || "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{leg.carrier || "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{leg.user_agent || "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>
                            {(leg.codecs || []).join(", ") || "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{leg.message_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {/* View-mode toggle + filter bar */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={viewMode}
                  onChange={(_e, v) => { if (v) setViewMode(v); }}
                  sx={{ "& .MuiToggleButton-root": { fontSize: 11, textTransform: "none", py: 0.25, px: 1 } }}
                >
                  <ToggleButton value="messages">Messages</ToggleButton>
                  <ToggleButton value="ladder">Ladder</ToggleButton>
                </ToggleButtonGroup>

                {viewMode === "messages" && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={errorsOnly}
                          onChange={(e) => setErrorsOnly(e.target.checked)}
                        />
                      }
                      label={<Typography sx={{ fontSize: 12, color: titleColor }}>Errors only</Typography>}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={methodFilter}
                        onChange={(e) => setMethodFilter(e.target.value)}
                        sx={{
                          fontSize: 12,
                          color: tableCellColor,
                          "& .MuiSelect-icon": { color: tableCellColor },
                        }}
                      >
                        <MenuItem value="ALL">All methods</MenuItem>
                        {methodsAvailable.map((m) => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: 11, color: subColor, ml: "auto" }}>
                      Showing {filteredMessages.length} of {messages.length}
                    </Typography>
                  </>
                )}
              </Box>

              {/* Ladder view */}
              {viewMode === "ladder" && (
                <Box sx={{ overflow: "auto", border: `1px solid ${borderColor}`, borderRadius: "6px", p: 1 }}>
                  <SipTraceLadderDiagram
                    messages={messages}
                    selectedCallId={ladderCallId}
                    onSelectCallId={setLadderCallId}
                    onViewMessage={(m) => setRawMsg(m)}
                    isDark={isDark}
                    serverIp={serverIp}
                  />
                </Box>
              )}

              {/* Messages table */}
              {viewMode === "messages" && (
              <Box sx={{ maxHeight: 360, overflow: "auto", border: `1px solid ${borderColor}`, borderRadius: "6px" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: tableHeadBg }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor, backgroundColor: tableHeadBg }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor, backgroundColor: tableHeadBg }}>Destination IP</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor, backgroundColor: tableHeadBg }}>Dir</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor, backgroundColor: tableHeadBg }}>Source IP</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor, backgroundColor: tableHeadBg }}>Method / Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor, backgroundColor: tableHeadBg }}>From</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor, backgroundColor: tableHeadBg }}>To</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor, backgroundColor: tableHeadBg }}>Remote IP</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 11, color: tableHeadColor, backgroundColor: tableHeadBg }}>Size</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMessages.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ textAlign: "center", fontSize: 12, color: subColor, borderBottomColor: borderColor, py: 3 }}>
                          No messages match the current filter.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredMessages.map((m) => {
                        const isErr = isErrorStatus(m.status_code);
                        const color = m.msg_type === "response"
                          ? SIP_STATUS_COLOR(m.status_code)
                          : SIP_METHOD_COLOR(m.method);
                        const label = m.msg_type === "response"
                          ? `${m.status_code || ""} ${m.status_text || ""}`.trim()
                          : m.method || "-";
                        const dirSymbol = m.direction === "received" ? "←" : "→";
                        return (
                          <TableRow
                            key={m.id}
                            hover
                            onClick={() => setRawMsg(m)}
                            sx={{
                              cursor: "pointer",
                              backgroundColor: isErr
                                ? (isDark ? "rgba(220,38,38,0.08)" : "rgba(220,38,38,0.04)")
                                : undefined,
                            }}
                          >
                            <TableCell sx={{ fontFamily: "monospace", fontSize: 11, color: tableCellColor, borderBottomColor: borderColor, whiteSpace: "nowrap" }}>
                              {(m.timestamp || "").replace("T", " ").slice(0, 19)}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, fontFamily: "monospace", color: tableCellColor, borderBottomColor: borderColor, whiteSpace: "nowrap" }}>
                              {serverIp || "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, color: tableCellColor, borderBottomColor: borderColor, textAlign: "center" }}>
                              {dirSymbol}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, fontFamily: "monospace", color: tableCellColor, borderBottomColor: borderColor, whiteSpace: "nowrap" }}>
                              {m.remote_ip || "—"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, color, fontWeight: 600, borderBottomColor: borderColor, whiteSpace: "nowrap" }}>
                              {label}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>
                              {m.from_user || m.from_header || "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>
                              {m.to_user || m.to_header || "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, fontFamily: "monospace", color: tableCellColor, borderBottomColor: borderColor, whiteSpace: "nowrap" }}>
                              {m.remote_ip || "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>
                              {m.bytes || "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </Box>
              )}

              {/* Raw message drawer */}
              <Drawer
                anchor="right"
                open={Boolean(rawMsg)}
                onClose={() => setRawMsg(null)}
                PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, backgroundColor: cardBg } }}
              >
                {rawMsg && (
                  <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: titleColor }}>
                        {rawMsg.msg_type === "response"
                          ? `${rawMsg.status_code || ""} ${rawMsg.status_text || ""}`.trim()
                          : rawMsg.method}
                        {" "}
                        <Chip
                          size="small"
                          label={rawMsg.direction === "received" ? "Received" : "Transmitted"}
                          sx={{ ml: 1, fontSize: 11 }}
                        />
                      </Typography>
                      <IconButton size="small" onClick={() => setRawMsg(null)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: subColor, fontFamily: "monospace", mb: 1 }}>
                      {rawMsg.timestamp} · {rawMsg.remote_ip}:{rawMsg.remote_port} · {rawMsg.transport}
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    <Box
                      component="pre"
                      sx={{
                        flex: 1,
                        overflow: "auto",
                        fontFamily: "monospace",
                        fontSize: 11,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        color: tableCellColor,
                        backgroundColor: isDark ? "#0f1822" : "#f8fafc",
                        p: 1.5,
                        borderRadius: "4px",
                        border: `1px solid ${borderColor}`,
                        m: 0,
                      }}
                    >
                      {rawMsg.raw || "(empty)"}
                    </Box>
                    <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => {
                          try { navigator.clipboard.writeText(rawMsg.raw || ""); } catch (_) { /* no-op */ }
                        }}
                      >
                        Copy raw
                      </Button>
                    </Box>
                  </Box>
                )}
              </Drawer>
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function SummaryStat({ label, value, accent, isDark }) {
  const labelColor = isDark ? "#8899aa" : "#6b7280";
  const valColor = accent || (isDark ? "#e0e0e0" : "#111827");
  return (
    <Box>
      <Typography sx={{ fontSize: 10, color: labelColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: valColor, fontFamily: "monospace" }}>
        {value === undefined || value === null || value === "" ? "—" : value}
      </Typography>
    </Box>
  );
}

export default function AdminCdrDetailsModal({
  open,
  onClose,
  uniqueid,
  audioControls,
  colorThem,
  selectedTz,
}) {
  const dispatch = useDispatch();
  const { loading, data, error } = useSelector(
    (state) => state.cdrDetails || { loading: false, data: null, error: null }
  );
  const isDark = colorThem === "theme_white";

  const paperBg = isDark ? "#1a2332" : "#fff";
  const bodyBg = isDark ? "#1a2332" : "#f9fafb";
  const cardBg = isDark ? "#1e2a3a" : "#fff";
  const titleColor = isDark ? "#e0e0e0" : "#111827";
  const subColor = isDark ? "#8899aa" : "#6b7280";
  const borderColor = isDark ? "#2a3a4a" : "#e5e7eb";
  const tableHeadBg = isDark ? "#1e2a3a" : "#f3f4f6";
  const tableHeadColor = isDark ? "#e0e0e0" : "#111827";
  const tableCellColor = isDark ? "#e0e0e0" : "#111827";
  const buttonBg = isDark ? "#2d5f42" : "#0a140e";
  const buttonHoverBg = isDark ? "#387252" : "#1c3326";
  const closeIconColor = isDark ? "#a0b4c8" : undefined;

  useEffect(() => {
    if (open && uniqueid) {
      dispatch(getAdminCdrDetails(uniqueid));
    }
    if (!open) {
      dispatch(resetAdminCdrDetails());
    }
  }, [open, uniqueid, dispatch]);

  const cdr = data?.cdr || null;
  const channelVars = data?.channel_vars || {};
  const hangupLog = data?.hangup_log || [];
  const buyerDialLog = data?.buyer_dial_log || [];
  // phone_lookup_cache record for this caller (E.164-matched on the backend).
  // null when no row was cached for the caller — section shows an N/A state.
  const phoneLookup = data?.phone_lookup || null;

  const highlightedVars = useMemo(() => {
    const entries = [];
    CHANNEL_VAR_HIGHLIGHTS.forEach((key) => {
      if (channelVars && channelVars[key] !== undefined) {
        entries.push([key, channelVars[key]]);
      }
    });
    return entries;
  }, [channelVars]);

  const allChannelVarEntries = useMemo(() => {
    if (!channelVars) return [];
    return Object.entries(channelVars).sort(([a], [b]) => a.localeCompare(b));
  }, [channelVars]);

  const handleExport = () => {
    if (!cdr) return;
    const safeCaller = (cdr.src || cdr.clid || "call").toString().replace(/[^\w.-]/g, "_");
    const abbr = getCurrentTzAbbreviation(selectedTz);
    const date = new Date().toISOString().slice(0, 10);
    const filename = `cdr_details_${safeCaller}_${date}_${abbr}.csv`;
    exportCdrDetailsToCSV(
      { cdr, channelVars, hangupLog, buyerDialLog },
      filename,
      selectedTz,
    );
  };

  const renderSummaryColumn = (fields) => (
    <Table size="small" sx={{ tableLayout: "fixed" }}>
      <TableBody>
        {fields.map(([label, key]) => (
          <KeyValueRow key={key} label={label} value={cdr?.[key]} isDark={isDark} />
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: paperBg,
          backgroundImage: "none",
          color: tableCellColor,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          borderBottom: `1px solid ${borderColor}`,
          backgroundColor: paperBg,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "18px", color: titleColor }}>
            Call Details
          </Typography>
          {cdr?.uniqueid && (
            <Typography
              sx={{ fontSize: "12px", color: subColor, fontFamily: "monospace" }}
            >
              {cdr.uniqueid}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            variant="contained"
            size="small"
            disabled={!cdr}
            sx={{
              backgroundColor: buttonBg,
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: buttonHoverBg },
            }}
          >
            Export CSV
          </Button>
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            variant="contained"
            size="small"
            sx={{
              backgroundColor: buttonBg,
              color: "#fff",
              textTransform: "none",
              "&:hover": { backgroundColor: buttonHoverBg },
            }}
          >
            Close
          </Button>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3, overflowY: "auto", backgroundColor: bodyBg }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Box sx={{ textAlign: "center", py: 6, color: isDark ? "#f87171" : "#b91c1c" }}>
            <Typography>{error}</Typography>
          </Box>
        )}
        {!loading && !error && !cdr && (
          <Box sx={{ textAlign: "center", py: 6, color: subColor }}>
            <Typography>No CDR record found for this call.</Typography>
          </Box>
        )}
        {!loading && cdr && (
          <>
            <SectionAccordion title="CDR Details" defaultExpanded isDark={isDark}>
              <Box sx={{ p: 2, backgroundColor: cardBg }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>{renderSummaryColumn(SUMMARY_FIELDS_LEFT)}</Grid>
                  <Grid item xs={12} md={4}>{renderSummaryColumn(SUMMARY_FIELDS_MID)}</Grid>
                  <Grid item xs={12} md={4}>{renderSummaryColumn(SUMMARY_FIELDS_RIGHT)}</Grid>
                </Grid>
                <Divider sx={{ my: 2, borderColor: borderColor }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontSize: 12, color: subColor, mb: 0.5 }}>
                      Call Start
                    </Typography>
                    {formatDateTimeJSXModal(cdr.start_at, selectedTz)}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontSize: 12, color: subColor, mb: 0.5 }}>
                      Call Answer
                    </Typography>
                    {formatDateTimeJSXModal(cdr.answer_at, selectedTz)}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ fontSize: 12, color: subColor, mb: 0.5 }}>
                      Call End
                    </Typography>
                    {formatDateTimeJSXModal(cdr.end_at, selectedTz)}
                  </Grid>
                </Grid>
                {cdr.recording_path && (
                  <>
                    <Divider sx={{ my: 2, borderColor: borderColor }} />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: isDark ? "#c0c8d0" : "#374151" }}>
                        Recording
                      </Typography>
                      <RecordingCell src={cdr.recording_path} audioControls={audioControls} />
                    </Box>
                  </>
                )}
              </Box>
            </SectionAccordion>

            {/* CallerId Lookup Info — pulls from phone_lookup_cache table on
                the backend, matched against cdr.src with E.164 variants.
                When no cached row exists the section shows an N/A state
                rather than hiding (so admins know the feature is there). */}
            <SectionAccordion title="CallerId Lookup Info" defaultExpanded isDark={isDark}>
              <Box sx={{ p: 2, backgroundColor: cardBg }}>
                {!phoneLookup ? (
                  <Typography
                    sx={{ fontSize: 13, color: subColor, textAlign: "center", py: 2 }}
                  >
                    N/A — no lookup data cached for this caller yet.
                  </Typography>
                ) : (
                  (() => {
                    const lt = String(phoneLookup.line_type || "").toUpperCase();
                    const lineTypeColor =
                      lt === "VOIP" ? "#1976d2" :
                      lt === "TOLL-FREE" ? "#f59e0b" :
                      lt === "MOBILE" ? "#2e7d32" :
                      lt === "LANDLINE" ? "#475569" : "#64748b";
                    const ne = (v) => v !== null && v !== undefined && String(v).trim() !== "";
                    const isFake = String(phoneLookup.fake_number || "").toUpperCase() === "YES";
                    const isOutsideUS = String(phoneLookup.outside_us || "").toUpperCase() === "YES";
                    const hasError = ne(phoneLookup.error_code) || ne(phoneLookup.error_description);
                    return (
                      <>
                        {/* Header row: phone number (mono) + cached-at timestamp */}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "baseline",
                            gap: 1.5,
                            mb: 1.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 15,
                              fontWeight: 700,
                              color: titleColor,
                            }}
                          >
                            {phoneLookup.phone_number}
                          </Typography>
                          {ne(phoneLookup.report_date) && (
                            <Typography sx={{ fontSize: 11, color: subColor }}>
                              cached {phoneLookup.report_date}
                            </Typography>
                          )}
                        </Box>

                        {/* Chip row — line type, country, outside-US, fake */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7, mb: 1.5 }}>
                          {ne(phoneLookup.line_type) && (
                            <Chip
                              label={phoneLookup.line_type}
                              size="small"
                              sx={{
                                background: lineTypeColor,
                                color: "#fff !important",
                                fontWeight: 700,
                                fontSize: 11,
                              }}
                            />
                          )}
                          {(ne(phoneLookup.country_code) || ne(phoneLookup.country)) && (
                            <Chip
                              label={
                                ne(phoneLookup.country_code) && ne(phoneLookup.country)
                                  ? `${phoneLookup.country_code} · ${phoneLookup.country}`
                                  : phoneLookup.country_code || phoneLookup.country
                              }
                              size="small"
                              sx={{
                                background: "#0a140e",
                                color: "#fff !important",
                                fontWeight: 600,
                                fontSize: 11,
                              }}
                            />
                          )}
                          {ne(phoneLookup.outside_us) && (
                            <Chip
                              label={`Outside US: ${phoneLookup.outside_us}`}
                              size="small"
                              sx={{
                                background: isOutsideUS ? "#7c3aed" : "#64748b",
                                color: "#fff !important",
                                fontWeight: 600,
                                fontSize: 11,
                              }}
                            />
                          )}
                          {isFake && (
                            <Chip
                              label="Fake Number"
                              size="small"
                              sx={{
                                background: "#dc2626",
                                color: "#fff !important",
                                fontWeight: 700,
                                fontSize: 11,
                              }}
                            />
                          )}
                        </Box>

                        {/* Reason text — only when fake_number is YES with a reason */}
                        {isFake && ne(phoneLookup.fake_number_reason) && (
                          <Box
                            sx={{
                              p: 1.2,
                              mb: 1.5,
                              background: "rgba(220,38,38,0.08)",
                              border: "1px solid rgba(220,38,38,0.35)",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#dc2626 !important",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                mb: 0.3,
                              }}
                            >
                              Fake-number reason
                            </Typography>
                            <Typography sx={{ fontSize: 12.5, color: tableCellColor }}>
                              {phoneLookup.fake_number_reason}
                            </Typography>
                          </Box>
                        )}

                        {/* Two-column: carrier / location key-value table */}
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Table size="small" sx={{ tableLayout: "fixed" }}>
                              <TableBody>
                                <KeyValueRow
                                  label="Carrier"
                                  value={ne(phoneLookup.phone_company) ? phoneLookup.phone_company : "—"}
                                  isDark={isDark}
                                />
                                <KeyValueRow
                                  label="Location"
                                  value={ne(phoneLookup.phone_location) ? phoneLookup.phone_location : "—"}
                                  isDark={isDark}
                                />
                              </TableBody>
                            </Table>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Table size="small" sx={{ tableLayout: "fixed" }}>
                              <TableBody>
                                <KeyValueRow
                                  label="Country"
                                  value={ne(phoneLookup.country) ? phoneLookup.country : "—"}
                                  isDark={isDark}
                                />
                                <KeyValueRow
                                  label="Country Code"
                                  value={ne(phoneLookup.country_code) ? phoneLookup.country_code : "—"}
                                  isDark={isDark}
                                />
                              </TableBody>
                            </Table>
                          </Grid>
                        </Grid>

                        {/* Lookup-itself errored — surface so admins know why
                            data is sparse for some numbers. */}
                        {hasError && (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.2,
                              background: "rgba(245,158,11,0.10)",
                              border: "1px solid rgba(245,158,11,0.40)",
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#92400e !important",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                mb: 0.3,
                              }}
                            >
                              Lookup error
                            </Typography>
                            <Typography
                              sx={{ fontSize: 12.5, color: tableCellColor, fontFamily: "monospace" }}
                            >
                              {ne(phoneLookup.error_code) ? phoneLookup.error_code : "(no code)"}
                              {ne(phoneLookup.error_description)
                                ? `: ${phoneLookup.error_description}`
                                : ""}
                            </Typography>
                          </Box>
                        )}
                      </>
                    );
                  })()
                )}
              </Box>
            </SectionAccordion>

            <SectionAccordion
              title="Call Flow Summary"
              defaultExpanded
              count={buyerDialLog.length}
              isDark={isDark}
            >
              <Box sx={{ backgroundColor: cardBg }}>
                {buyerDialLog.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: "center", color: subColor, fontSize: 13 }}>
                    No buyer dial log for this call.
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: tableHeadBg }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Buyer</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Number</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Dial String</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Timeout</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Actual</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {buyerDialLog.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{row.buyer_name || "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{row.buyer_number || "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontFamily: "monospace", color: tableCellColor, borderBottomColor: borderColor }}>
                            {row.dialstring || "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{row.status || "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{row.timeout ?? "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{row.actual_timeout ?? "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>
                            {formatDateTimeJSXModal(row.created_at, selectedTz)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </SectionAccordion>

            <SectionAccordion title="Channel Vars" count={highlightedVars.length} isDark={isDark}>
              <Box sx={{ p: 2, backgroundColor: cardBg }}>
                {highlightedVars.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: subColor, textAlign: "center", py: 2 }}>
                    No highlighted channel vars captured for this call.
                  </Typography>
                ) : (
                  <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <TableBody>
                      {highlightedVars.map(([k, v]) => (
                        <KeyValueRow key={k} label={k} value={v} mono isDark={isDark} />
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </SectionAccordion>

            <SectionAccordion title="Details" count={allChannelVarEntries.length} isDark={isDark}>
              <Box sx={{ p: 2, backgroundColor: cardBg }}>
                {allChannelVarEntries.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: subColor, textAlign: "center", py: 2 }}>
                    No channel variables captured for this call.
                  </Typography>
                ) : (
                  <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <TableBody>
                      {allChannelVarEntries.map(([k, v]) => (
                        <KeyValueRow key={k} label={k} value={v} mono isDark={isDark} />
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </SectionAccordion>

            <SipLogsSection
              uniqueid={cdr?.uniqueid}
              isDark={isDark}
              cardBg={cardBg}
              borderColor={borderColor}
              subColor={subColor}
              tableHeadBg={tableHeadBg}
              tableHeadColor={tableHeadColor}
              tableCellColor={tableCellColor}
              titleColor={titleColor}
            />

            <SectionAccordion title="Hangupcause Log" count={hangupLog.length} isDark={isDark}>
              <Box sx={{ backgroundColor: cardBg }}>
                {hangupLog.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: "center", color: subColor, fontSize: 13 }}>
                    No hangup events captured for this call.
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: tableHeadBg }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Event</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Cause</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Cause Text</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 12, color: tableHeadColor }}>Channel</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {hangupLog.map((h, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ fontSize: 12, fontFamily: "monospace", color: tableCellColor, borderBottomColor: borderColor }}>
                            {h.timestamp || "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{h.event || "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{h.cause ?? "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: tableCellColor, borderBottomColor: borderColor }}>{h.cause_txt || "-"}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontFamily: "monospace", color: tableCellColor, borderBottomColor: borderColor }}>
                            {h.channel || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </SectionAccordion>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
